// Keeps toast notifications inside the user's visible viewport when the app runs
// inside an iframe.
//
// The host page (GN for Business) sizes the iframe to its full content height, so
// `position: fixed` (used by vue-toastification) is relative to the whole tall
// iframe, not the slice the user is looking at. A `BOTTOM_CENTER` toast therefore
// lands at the bottom of the *entire* content, off-screen until the user scrolls.
//
// This mirrors the working solution in the sibling repo `pro-config-ui`
// (`index.html`). The protocol is two-way:
//   1. The iframe resolves the host origin from `document.referrer` and posts its
//      content `{ height }` to the host (on load, resize, and DOM changes). The
//      host uses this to size the iframe and to know its content geometry.
//   2. The host posts back the currently-visible rectangle of the iframe
//      (`iframeVisibleSpaceTop` / `iframeVisibleHeight`). We cache it and anchor the
//      toast container to the bottom of that rectangle via a CSS variable + gating
//      class.
//
// Establishing step 1 is what was missing before: on QA/prod only a wildcard origin
// is configured, so `postMessage` has no concrete target until the host origin is
// resolved from the referrer — and without our height messages the host never
// broadcast the visible rectangle back to this iframe.
//
// The CSS side of this lives in `src/assets/toast-position.css`.

const TOAST_ANCHOR_VAR = '--dp-toast-anchor-top';
const ANCHORED_CLASS = 'dp-toast-anchored';
const BOTTOM_MARGIN_PX = 16;
// Extra room reported with the content height, matching pro-config-ui.
const CONTENT_MARGIN_PX = 60;
const SESSION_KEY_TOP = 'iframeVisibleSpaceTop';
const SESSION_KEY_HEIGHT = 'iframeVisibleHeight';

export interface TrustedOrigins {
  exactOrigins: string[];
  wildcardSuffixes: string[];
}

interface ViewportMessage {
  iframeVisibleSpaceTop: number;
  iframeVisibleHeight: number;
}

// The host origin verified from the referrer or a received message, used as the
// concrete `postMessage` target (a wildcard suffix cannot be a target origin).
let verifiedParentOrigin: string | null = null;
// Deduplicates height messages so DOM mutations don't spam the host.
let lastHeightSentToParent: number | null = null;
let heightSendFrameId = 0;

/**
 * Parses `VUE_APP_TRUSTED_PARENT_ORIGINS` (a JSON array or comma-separated list)
 * into exact origins and `*.domain` wildcard suffixes.
 */
export function parseTrustedOrigins(value: string | undefined): TrustedOrigins {
  const empty: TrustedOrigins = { exactOrigins: [], wildcardSuffixes: [] };
  if (typeof value !== 'string' || value.trim() === '') {
    return empty;
  }

  let entries: string[] = [];
  const trimmedValue = value.trim();
  try {
    const parsed = JSON.parse(trimmedValue);
    entries = Array.isArray(parsed) ? parsed : trimmedValue.split(',');
  } catch {
    entries = trimmedValue.split(',');
  }

  const exactOrigins: string[] = [];
  const wildcardSuffixes: string[] = [];

  entries.forEach((entry) => {
    if (typeof entry !== 'string') return;
    const trimmed = entry.trim();
    if (trimmed === '') return;

    const isWildcard = trimmed.includes('*.');
    if (isWildcard) {
      const suffix = trimmed
        .replace(/^https?:\/\//, '')
        .replace(/^\*\./, '.')
        .replace(/\/$/, '');
      if (suffix) wildcardSuffixes.push(suffix.toLowerCase());
      return;
    }

    try {
      const url = new URL(trimmed);
      // Only http(s) origins. Other schemes (file:, data:, sandboxed) expose the
      // opaque-origin sentinel "null", which would then match a "null" sender.
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
      exactOrigins.push(url.origin);
    } catch {
      // Skip invalid URLs.
    }
  });

  return {
    exactOrigins: Array.from(new Set(exactOrigins)),
    wildcardSuffixes: Array.from(new Set(wildcardSuffixes)),
  };
}

/**
 * Returns true when `origin` is an allowed parent origin. Wildcard suffixes are
 * only honoured over HTTPS.
 */
export function isTrustedParentOrigin(
  origin: string,
  trusted: TrustedOrigins
): boolean {
  if (typeof origin !== 'string' || origin === '') return false;

  if (trusted.exactOrigins.includes(origin)) return true;

  try {
    const url = new URL(origin);
    if (url.protocol !== 'https:') return false;
    const hostname = url.hostname.toLowerCase();
    return trusted.wildcardSuffixes.some((suffix) => hostname.endsWith(suffix));
  } catch {
    return false;
  }
}

/**
 * Computes the `top` anchor (in px) for the toast container's bottom edge, so the
 * toast sits just inside the bottom of the visible band. Clamped to the document
 * height so it never lands below the content.
 */
export function computeAnchorTop(
  visibleSpaceTop: number,
  visibleHeight: number,
  documentHeight: number
): number {
  const anchorTop = visibleSpaceTop + visibleHeight - BOTTOM_MARGIN_PX;
  const maxTop =
    documentHeight > 0 ? documentHeight - BOTTOM_MARGIN_PX : anchorTop;
  return Math.max(0, Math.min(anchorTop, maxTop));
}

function isViewportMessage(data: unknown): data is ViewportMessage {
  if (typeof data !== 'object' || data === null) return false;
  const candidate = data as Record<string, unknown>;
  // `typeof NaN`/`typeof Infinity` are both 'number', so require finite values to
  // avoid writing an invalid CSS length (e.g. "NaNpx") for a malformed message.
  return (
    typeof candidate.iframeVisibleSpaceTop === 'number' &&
    Number.isFinite(candidate.iframeVisibleSpaceTop) &&
    typeof candidate.iframeVisibleHeight === 'number' &&
    Number.isFinite(candidate.iframeVisibleHeight)
  );
}

/** Full content height of this document, plus a margin, matching pro-config-ui. */
function getContentHeight(): number {
  const height = Math.max(
    document.documentElement.scrollHeight,
    document.body?.scrollHeight ?? 0
  );
  return height > 0 ? height + CONTENT_MARGIN_PX : 0;
}

/**
 * Resolves the host origin from `document.referrer` when it is trusted. This is the
 * only way to obtain a concrete `postMessage` target when just a wildcard origin is
 * configured (QA/prod).
 */
export function resolveParentOriginFromReferrer(
  trusted: TrustedOrigins
): string | null {
  try {
    const referrerOrigin = new URL(document.referrer).origin;
    if (isTrustedParentOrigin(referrerOrigin, trusted)) return referrerOrigin;
  } catch {
    // No valid referrer available.
  }
  return null;
}

/** Posts a message to the verified parent, or to every configured exact origin. */
function postMessageToTrustedParents(
  message: unknown,
  trusted: TrustedOrigins
): void {
  if (verifiedParentOrigin) {
    window.parent.postMessage(message, verifiedParentOrigin);
    return;
  }
  trusted.exactOrigins.forEach((origin) => {
    window.parent.postMessage(message, origin);
  });
}

/** Sends the current content height to the host, deduplicating repeat values. */
function sendHeightToParent(trusted: TrustedOrigins): void {
  const finalHeight = getContentHeight();
  if (finalHeight <= 0 || finalHeight === lastHeightSentToParent) return;
  if (!verifiedParentOrigin && trusted.exactOrigins.length === 0) return;

  lastHeightSentToParent = finalHeight;
  postMessageToTrustedParents({ height: finalHeight }, trusted);
}

/** Anchors the toast container to the bottom of the given visible rectangle. */
function applyAnchor(visibleSpaceTop: number, visibleHeight: number): void {
  const anchorTop = computeAnchorTop(
    visibleSpaceTop,
    visibleHeight,
    getContentHeight()
  );

  const root = document.documentElement;
  const nextValue = `${anchorTop}px`;

  // The MutationObserver watches attributes on documentElement, so writing the
  // style/class here would queue a mutation → scheduleHeightReport → rAF →
  // applyAnchor again → an unbounded rAF loop. Only touch the DOM when the value
  // actually changes so a repeated anchor is a genuine no-op and the loop settles.
  if (root.style.getPropertyValue(TOAST_ANCHOR_VAR) !== nextValue) {
    root.style.setProperty(TOAST_ANCHOR_VAR, nextValue);
  }
  if (!root.classList.contains(ANCHORED_CLASS)) {
    root.classList.add(ANCHORED_CLASS);
  }
}

/** Reads the cached visible rectangle, if any and still valid. */
function readCachedViewport(): ViewportMessage | null {
  let cachedTop: string | null = null;
  let cachedHeight: string | null = null;
  try {
    cachedTop = sessionStorage.getItem(SESSION_KEY_TOP);
    cachedHeight = sessionStorage.getItem(SESSION_KEY_HEIGHT);
  } catch {
    return null;
  }
  if (cachedTop === null || cachedHeight === null) return null;

  const iframeVisibleSpaceTop = Number(cachedTop);
  const iframeVisibleHeight = Number(cachedHeight);
  if (
    !Number.isFinite(iframeVisibleSpaceTop) ||
    !Number.isFinite(iframeVisibleHeight)
  ) {
    return null;
  }
  return { iframeVisibleSpaceTop, iframeVisibleHeight };
}

/** Re-anchors the toast from the cached rectangle; no-op when nothing is cached. */
function applyAnchorFromCache(): void {
  const cached = readCachedViewport();
  if (cached) {
    applyAnchor(cached.iframeVisibleSpaceTop, cached.iframeVisibleHeight);
  }
}

/**
 * Handles one `message` event from the host: caches the verified origin and the
 * visible rectangle, then repositions the toast — but only when it comes from the
 * trusted parent and carries a valid viewport payload; ignores everything else.
 */
export function handleParentMessage(
  event: MessageEvent,
  trusted: TrustedOrigins
): void {
  if (event.source !== window.parent) return;
  if (!isTrustedParentOrigin(event.origin, trusted)) return;

  // Cache the verified parent origin for postMessage replies (height updates).
  verifiedParentOrigin = event.origin;

  if (!isViewportMessage(event.data)) return;

  const { iframeVisibleSpaceTop, iframeVisibleHeight } = event.data;

  try {
    // Cache so a later toast (arriving between host messages) can re-anchor.
    sessionStorage.setItem(SESSION_KEY_TOP, String(iframeVisibleSpaceTop));
    sessionStorage.setItem(SESSION_KEY_HEIGHT, String(iframeVisibleHeight));
  } catch {
    // sessionStorage unavailable; the live values below still anchor the toast.
  }

  // Anchor from the live values so it works even when sessionStorage is blocked.
  applyAnchor(iframeVisibleSpaceTop, iframeVisibleHeight);
}

/** Schedules a debounced height report on the next animation frame. */
function scheduleHeightReport(trusted: TrustedOrigins): void {
  if (heightSendFrameId) return;
  heightSendFrameId = window.requestAnimationFrame(() => {
    heightSendFrameId = 0;
    sendHeightToParent(trusted);
    // Content growth can shift the visible band; re-anchor from the latest cache.
    applyAnchorFromCache();
  });
}

/**
 * Starts the two-way iframe protocol: reports this iframe's height to the host and
 * repositions toasts into the user's visible viewport from the host's messages.
 * Safe to call when not embedded in an iframe: height reporting and observers are
 * skipped, and no trusted messages arrive, so nothing changes.
 */
export function initIframeToastPosition(): void {
  const trusted = parseTrustedOrigins(
    process.env.VUE_APP_TRUSTED_PARENT_ORIGINS
  );

  // Always listen — harmless when not framed (no external origin is trusted).
  window.addEventListener('message', (event) =>
    handleParentMessage(event, trusted)
  );

  // Not in an iframe: nothing to report or observe.
  if (window.self === window.top) return;

  verifiedParentOrigin = resolveParentOriginFromReferrer(trusted);

  sendHeightToParent(trusted);
  window.addEventListener('resize', () => scheduleHeightReport(trusted));

  const observer = new MutationObserver(() => scheduleHeightReport(trusted));
  observer.observe(document.documentElement, {
    attributes: true,
    childList: true,
    characterData: true,
    subtree: true,
  });
}
